<template>
  <div>
    <!-- Tab nav — sits above the toolbar so changing tabs preserves
         filter state (the toolbar's local picker state is reseeded
         from the store on each child mount, but the store survives
         tab switches). -->
    <div class="bg-white rounded-xl border border-gray-200/60 mb-4 card-shadow">
      <nav class="flex items-center gap-1 px-3 pt-2 pb-2">
        <router-link
          v-for="t in tabs"
          :key="t.key"
          :to="{ name: t.routeName }"
          :class="[
            'inline-flex items-center gap-2 px-4 h-[34px] rounded-lg text-[13px] font-medium transition-all duration-150',
            isActive(t.key)
              ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
          ]"
        >
          <span v-html="t.icon" class="inline-flex"></span>
          {{ t.label }}
        </router-link>
      </nav>
    </div>

    <!-- Toolbar — Ads tab uses its own dedicated toolbar (Ad-type
         select + Live/Media switch + conditional Live or Media
         selectors) because the filter shape diverges enough that
         shoehorning into the shared one would muddy both. Every other
         tab keeps the shared AnalyticsToolbar. Both dispatch APPLY
         into the same analytics store on Search; child views watch
         `applied.*` regardless of which toolbar drove the change. -->
    <AdsToolbar v-if="activeTab === 'ads'" :loading="loading" />
    <AnalyticsToolbar v-else :tab="activeTab" :loading="loading" @download="onDownload" />

    <!-- Active tab body. Children read filters from store and report
         their loading state up via the `loading` event so the
         toolbar's Search button can show a spinner. -->
    <router-view v-slot="{ Component }">
      <component :is="Component" ref="tabView" @loading="onChildLoading" />
    </router-view>
  </div>
</template>

<script>
import AnalyticsToolbar from './components/AnalyticsToolbar.vue'
import AdsToolbar from './components/AdsToolbar.vue'
import { downloadCsv, downloadPdf } from './report-export.js'
import { notifyError } from '../../services/notifications.js'

/**
 * Analytics section shell. Holds the tab nav + the shared toolbar
 * above whichever child view is active. Three tabs:
 *
 *   /analytics/overview  →  cross-stream digest, no entity filters
 *   /analytics/lives     →  per-live (and per-show) detail
 *   /analytics/medias    →  per-media (and per-format) detail
 *
 * Filter state lives in the `analytics` Vuex module so it survives
 * tab switches — going Lives → Overview → Lives keeps your last
 * date range and live selection intact.
 */
export default {
  name: 'Analytics',
  components: { AnalyticsToolbar, AdsToolbar },
  data() {
    return {
      loading: false,
      tabs: [
        {
          key: 'overview',
          label: 'Overview',
          routeName: 'analytics-overview',
          icon: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>'
        },
        {
          key: 'lives',
          label: 'Lives',
          routeName: 'analytics-lives',
          icon: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>'
        },
        {
          key: 'medias',
          label: 'Medias',
          routeName: 'analytics-medias',
          icon: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/></svg>'
        },
        {
          key: 'ads',
          label: 'Ads',
          routeName: 'analytics-ads',
          icon: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 11-5.8-1.6"/></svg>'
        }
      ]
    }
  },
  computed: {
    /**
     * Current tab key — resolved from the route name. Drives the
     * toolbar's conditional selectors and the tab-nav active style.
     */
    activeTab() {
      const map = {
        'analytics-overview': 'overview',
        'analytics-lives':    'lives',
        'analytics-medias':   'medias',
        'analytics-ads':      'ads'
      }
      return map[this.$route.name] || 'overview'
    }
  },
  methods: {
    isActive(key) {
      return this.activeTab === key
    },
    onChildLoading(v) {
      this.loading = !!v
    },
    /** Toolbar Download → ask the active tab for its report, then export. */
    async onDownload({ format, subject, filters }) {
      const report = this.$refs.tabView?.buildReport?.()
      if (!report || (!report.kpis.length && !report.timeline)) {
        notifyError('Nothing to download yet. Run a search first.')
        return
      }
      report.subtitle = subject || ''
      report.filters = filters
      try {
        if (format === 'csv') downloadCsv(report, { appName: `${this.$appName} Platform` })
        else await downloadPdf(report, { appName: `${this.$appName} Platform` })
      } catch (err) {
        notifyError(err?.message || 'Could not create the report')
      }
    }
  },

  /**
   * Reset filters on ENTRY, not on leave. Doing it in beforeRouteLeave
   * mutated `applied` while the child was still mounted — the child's
   * deep watcher then fired `loadAnalytics()`, which built a fresh
   * AbortController and dispatched a KPI request microseconds before
   * unmount aborted it. The canceled request surfaced as the global
   * "connection lost" modal. Resetting in `created()` runs before any
   * child is instantiated (parent created → child created → child
   * mounted), so the reset can't cascade into a fetch, and the child
   * mounts with the fresh filters already in place. Tab switches
   * (overview ↔ lives ↔ medias ↔ ads) keep the parent mounted, so
   * `created()` only fires on the first entry from outside analytics.
   */
  created() {
    this.$store.dispatch('analytics/resetAll')
  }
}
</script>
